// src/features/developer/utils/base64Utils.ts
import { saveAs } from 'file-saver';

/**
 * Converts a File object to a Base64 string.
 * @param file The file to convert.
 * @param includePrefix Whether to include the 'data:mime/type;base64,' prefix.
 * @returns A promise that resolves with the Base64 string.
 */
export const fileToBase64 = (file: File, includePrefix: boolean): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            let result = reader.result as string;
            if (!includePrefix) {
                // Find the comma that separates the prefix from the data
                const commaIndex = result.indexOf(',');
                if (commaIndex !== -1) {
                    result = result.substring(commaIndex + 1);
                }
            }
            resolve(result);
        };
        reader.onerror = error => reject(error);
    });
};

/**
 * Decodes a Base64 string to its binary data and determines its MIME type.
 * @param base64String The Base64 string to decode.
 * @returns An object containing the decoded data as a Uint8Array and the MIME type.
 */
export const decodeBase64 = (base64String: string): { data: Uint8Array, mimeType: string } => {
    let dataPart = base64String.trim();
    let mimeType = 'application/octet-stream'; // Default MIME type

    // Check for and strip the data URI prefix if it exists
    const prefixMatch = dataPart.match(/^data:([a-zA-Z0-9]+\/[a-zA-Z0-9-.+]+);base64,/);
    if (prefixMatch) {
        mimeType = prefixMatch[1];
        dataPart = dataPart.substring(prefixMatch[0].length);
    }

    try {
        // Use atob for decoding
        const binaryString = window.atob(dataPart);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return { data: bytes, mimeType };
    } catch (error) {
        console.error("Failed to decode Base64 string:", error);
        throw new Error('Invalid Base64 string');
    }
};

/**
 * Creates a Blob from a Base64 string.
 * @param base64String The Base64 string.
 * @param manualMimeType An optional manually provided MIME type to override detection.
 * @returns A Blob object.
 */
export const base64ToBlob = (base64String: string, manualMimeType?: string): Blob => {
    const { data, mimeType } = decodeBase64(base64String);
    return new Blob([data], { type: manualMimeType || mimeType });
};


/**
 * Triggers a file download from a Base64 string.
 * @param base64String The Base64 string.
 * @param fileName The desired name for the downloaded file.
 * @param manualMimeType Optional manual MIME type.
 */
export const saveBase64AsFile = (base64String: string, fileName: string, manualMimeType?: string) => {
    const blob = base64ToBlob(base64String, manualMimeType);
    saveAs(blob, fileName);
};